import { ReactElement, useEffect, useRef, useState } from "react";
import { Anchor } from "@twilio-paste/core/anchor";
import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { styled } from "@twilio-paste/core/styling-library";
import { ClearEditorPlugin } from "@twilio-paste/core/lexical-library";
import { HelpText } from "@twilio-paste/core/help-text";

import { ArrowDownIcon } from "@twilio-paste/icons/esm/ArrowDownIcon";

import { useForm, Controller } from "react-hook-form";

import {
  WiseOwlBlock,
  dataTestIds,
  trackingDetails,
  EmptyStateVariant,
  NewAiMessageStatus,
  WiseOwlChatProps,
  SendMessageFormValues,
  ChatMessageData,
  NewChatFormValues,
  ChatFormLabels,
  WiseOwlElementIds,
  WiseOwlBlockType,
  WiseOwlIngress,
  ChatButtonToolType,
  WiseOwlApplicationEnum,
} from "@twiliointernal/e2-commons-shared-definitions";
import { useScroll } from "@twiliointernal/e2-commons-shared-hooks";
import {
  EmptyState,
  CompositeChatComposer,
  ChatComposerPlugins,
} from "@twiliointernal/e2-commons-shared-ui";
import {
  LiveChatComposer,
  LiveChatPreEngagement,
  EndChat,
} from "@twiliointernal/e2-commons-live-chat";
import {
  AssistantFeedbackButton,
  CsatSurvey,
  useSurveyContext,
} from "@twiliointernal/e2-commons-csat-survey";

import { copy, constants } from "./definitions";
import {
  ChatLog,
  NewPreChat,
  DeprecatedPreChat,
  PreChatPromptSuggestions,
  PromptLibraryContainer,
} from "../../components";
import { useWiseOwlContext } from "../../hooks";

import {
  lastMessageFromAuthor,
  hasMessages,
} from "../../components/blockRenderer/helpers";

import {
  INPUT_PROMPT_LIBRARY_DELAY,
  MIN_INPUT_LENGTH,
} from "./definitions/constants";

const WiseOwlAppContainer = styled(Box)<{
  isChatActive?: boolean;
  ingressOkForStyle?: boolean;
}>`
  // The UI layout is different when the chat is active vs. pre Chat state.
  flex: ${(props) => (props.isChatActive ? 1 : undefined)};
  height: ${(props) =>
    // Height set to 100% here breaks the autoscroll on IDA, but not other ingresses
    props.isChatActive && props.ingressOkForStyle ? "100%" : undefined};

  .paste-theme-provider {
    height: 100%;
  }
`;
const WiseOwlChat = ({
  blocks,
  chatId,
  chatMessages,
  newAiMessageStatus = NewAiMessageStatus.None,
  loadingConfiguration,
  variantOptions,
  onStartNewChat,
  onChatButtonToolClick,
  onDisplayPromptLibrary,
  onSendChatMessage,
  disabled = false,
}: WiseOwlChatProps): ReactElement => {
  const wiseOwlContext = useWiseOwlContext();
  const {
    handlers: {
      handleTrackEvent,
      handleTransferToLiveChat,
      handleToggleLiveChatPreEngagement,
      handleContextInit,
    },
    applicationProps: {
      applicationId,
      displayName,
      ingress,
      scrollContainerElementId,
    },
    isLiveChatPreEngagementOpen,
    liveChatConfiguration,
  } = wiseOwlContext;

  /**
   * Allows consuming applications to pass a preferred scroll container,
   * if none is provided, fallback to the WISE_OWL_CHAT_LOG_CONTAINER
   * (this is the default for most WiseOwl implementations)
   */
  const wiseOwlScrollContainer = document.querySelector(
    `[data-paste-element="${
      scrollContainerElementId || WiseOwlElementIds.WISE_OWL_CHAT_LOG_CONTAINER
    }"]`
  ) as HTMLElement;
  const {
    maxScroll,
    currentScroll,
    scrollToBottom,
    updateScrollState,
    updateScrollBottom,
  } = useScroll(wiseOwlScrollContainer);
  const [showNewMessageCta, setShowNewMessageCta] = useState<boolean>(false);
  const autoScrollOnAiMessage = useRef<boolean>(true);
  const isChatActive = !!chatId || hasMessages(blocks);
  const [isLiveChatActive, setIsLiveChatActive] = useState<boolean>(false);
  const [forceScrollDown, setForceScrollDown] = useState<boolean>(true);
  const [displayPromptLibrary, setDisplayPromptLibrary] =
    useState<boolean>(false);
  const [promptSuggestion, setPromptSuggestion] = useState<string>("");
  const [openPromptLibrary, setOpenPromptLibrary] = useState<boolean>(false);

  const {
    state: { isSurveyEnabled, isSurveyVisible, surveyId },
    handlers: { handleShowSurvey, resetSurveyState },
  } = useSurveyContext();

  // form and field initialization
  const { handleSubmit, control, watch, formState, reset, setValue } =
    useForm<SendMessageFormValues>();

  const { isValid, isSubmitting, errors, isSubmitted, isSubmitSuccessful } =
    formState;
  const inputHasMinLengthError = errors?.input?.type === "minLength";
  const inputFieldValue = watch("input");
  const chatIdFieldValue = watch("chatId");
  const newAiMessageComplete =
    newAiMessageStatus === NewAiMessageStatus.Complete;
  const newAiMessageLoading = newAiMessageStatus === NewAiMessageStatus.Loading;
  const isVoiceInsightsPage =
    applicationId === WiseOwlApplicationEnum.voiceInsights;
  const isConsoleIngress =
    ingress === WiseOwlIngress.console && !isVoiceInsightsPage;

  const renderAriaLabel: { [key: string]: string } = {
    [WiseOwlIngress.console]: isVoiceInsightsPage
      ? copy.fields.input.ariaLabel
      : copy.fields.deprecatedInput.ariaLabel,
    [WiseOwlIngress.wiseOwlApp]: copy.fields.wiseOwlAppInput.ariaLabel,
    default: copy.fields.input.ariaLabel,
  };

  const renderPlaceholder: { [key: string]: string } = {
    [WiseOwlIngress.console]: isVoiceInsightsPage
      ? copy.fields.input.placeholder
      : copy.fields.deprecatedInput.placeholder,
    [WiseOwlIngress.wiseOwlApp]: copy.fields.wiseOwlAppInput.placeholder,
    default: copy.fields.input.placeholder,
  };

  const ingressWithCustomPlaceholder: Set<WiseOwlIngress> = new Set([
    WiseOwlIngress.console,
    WiseOwlIngress.wiseOwlApp,
  ]);

  const placeholderToUse = ingressWithCustomPlaceholder.has(ingress)
    ? ingress
    : "default";

  // WOBK - integrates chatId state as form value for handling sending message.
  useEffect(() => {
    // only set this value once
    if (!chatIdFieldValue && !!chatId) {
      setValue("chatId", chatId);
    }
  }, [chatId]);

  /**
   * Reset chat message input only on successful submission
   */
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(undefined, { keepDirtyValues: true });
      setPromptSuggestion("");
    }
  }, [isSubmitSuccessful, inputFieldValue, reset]);

  /**
   * - Toggle New Message CTA based on scroll position and
   *  new message status
   * - Toggle Auto-scroll
   */
  useEffect(() => {
    if (!isUserViewingLatestMessages() && newAiMessageComplete) {
      setShowNewMessageCta(true);
    } else {
      toggleAutoScroll();
    }
  }, [newAiMessageStatus]);

  /**
   * - Hide New Message CTA if viewing new messages
   * - Toggle Auto-scroll
   */
  useEffect(() => {
    if (isUserViewingLatestMessages()) {
      setShowNewMessageCta(false);
    }
    toggleAutoScroll();
  }, [currentScroll]);

  /**
   * Set is chat active and call scroll/New Message CTA handlers
   * based on incoming blocks and legacy chat messages
   */
  useEffect(() => {
    updateScrollState();
    if (blocks.length) {
      const liveChatBlock = blocks?.find(
        (block) => block.type === WiseOwlBlockType.LiveChat
      );
      setIsLiveChatActive(!!liveChatBlock);

      handleScrollToBottom(blocks);
    } else if (chatMessages?.length) {
      handleLegacyChatMessages(chatMessages);
    }

    if (isSurveyVisible) {
      scrollToBottom();
    }
  }, [blocks, chatMessages, isSurveyVisible, maxScroll, forceScrollDown]);

  useEffect(() => {
    // Reset forceScrollDown
    if (!forceScrollDown && !loadingConfiguration.submission) {
      setForceScrollDown(true);
    }
  }, [loadingConfiguration]);

  /**
   * Tracks WiseOwl context initialization, and
   * then calls provided handler if it exists
   */
  useEffect(() => {
    const { meta: { initialized } = {} } = wiseOwlContext;

    if (initialized && handleContextInit) {
      handleContextInit(wiseOwlContext);
    }
  }, [wiseOwlContext.meta.initialized]);

  useEffect(() => {
    let displayPromptTimeout: NodeJS.Timeout;
    if (inputFieldValue?.startsWith("/") && displayPromptLibrary) {
      displayPromptTimeout = setTimeout(() => {
        onDisplayPromptLibraryCalled();
      }, INPUT_PROMPT_LIBRARY_DELAY);
    } else if (!inputFieldValue?.startsWith("/") && displayPromptLibrary) {
      setDisplayPromptLibrary(false);
      setOpenPromptLibrary(false);
    }

    return () => clearTimeout(displayPromptTimeout);
  }, [inputFieldValue]);

  const handleScrollToBottom = (blocks: WiseOwlBlock[]): void => {
    // scroll to bottom if last message was from author / if AI
    // auto-scrolling is engaged.
    if (lastMessageFromAuthor(blocks) || autoScrollOnAiMessage.current) {
      scrollToBottom();
      setShowNewMessageCta(false);
    }

    if (forceScrollDown && hasMessages(blocks)) {
      updateScrollBottom();
      setForceScrollDown(false);
    }
  };

  // Handle scrolling and showing New Messages CTA for chat messages (legacy)
  const handleLegacyChatMessages = (chatMessages: ChatMessageData[]): void => {
    const { author } = chatMessages[chatMessages.length - 1];
    if (!author.isAuthor) {
      setShowNewMessageCta(isUserViewingLatestMessages() ? false : true);
    } else {
      scrollToBottom();
    }
  };

  /**
   * CTA Handlers
   */
  const handleMessageSentTrackEvent = (eventName: string): void => {
    if (handleTrackEvent) {
      handleTrackEvent(eventName, { chatId, ingress });
    }
  };

  const handleStartNewChat = (data: NewChatFormValues): void => {
    // Make sure survey state is reset when starting a new chat
    resetSurveyState();

    if (onStartNewChat) {
      onStartNewChat(data);
    }

    handleMessageSentTrackEvent(
      trackingDetails.wiseOwl.chatCreated(displayName)
    );
  };

  const handleSendChatMessage = (data: SendMessageFormValues): void => {
    // we want users to be able to submit a new survey if they send a new message after previously submitting a survey
    resetSurveyState();

    if (onSendChatMessage) {
      // the legacy (no blocks) implementation of the API does not require
      // passing in chatId
      onSendChatMessage(chatId ? { ...data, chatId } : data);
    }

    handleMessageSentTrackEvent(
      trackingDetails.wiseOwl.messageSent(displayName)
    );
  };

  const handleClickNewMessage = () => {
    scrollToBottom();
    setShowNewMessageCta(false);
  };

  const formHandler = isChatActive ? handleSendChatMessage : handleStartNewChat;

  const isUserViewingLatestMessages = () => {
    // if the chat log has no overflow, by default user is viewing latest messages
    if (currentScroll === 0 && maxScroll === 0) {
      return true;
    }

    return currentScroll / maxScroll > constants.VIEWING_LATEST_THRESHOLD;
  };

  const toggleAutoScroll = () => {
    if (isUserViewingLatestMessages()) {
      autoScrollOnAiMessage.current = newAiMessageLoading;
      setShowNewMessageCta(false);
    } else {
      autoScrollOnAiMessage.current = false;
    }
  };

  const handleStartLiveChatEngagement = async (
    productCategory: string,
    message: string
  ): Promise<void> => {
    // Make sure survey state is reset when starting a new chat.
    resetSurveyState();

    if (handleTransferToLiveChat) {
      handleTransferToLiveChat(productCategory, message);
    }
  };

  const renderScrollToNewMessageButton = () => (
    <Box
      display="flex"
      position="absolute"
      bottom="15%"
      left="50%"
      transform="translate(-50%, -50%)"
      zIndex="zIndex10"
    >
      <Button
        variant="primary"
        size="rounded_small"
        onClick={handleClickNewMessage}
      >
        {copy.ctas.newMessage}
        <ArrowDownIcon decorative />
      </Button>
    </Box>
  );

  const renderStateVariant = () => {
    if (!variantOptions) {
      return;
    }

    const { variant, ctaText, ctaUrl } = variantOptions;

    if (variant === EmptyStateVariant.oops) {
      return (
        <EmptyState
          variant={variant}
          heading={copy.variants.oopsTitle}
          description={copy.variants.oopsDescription}
        >
          <Anchor href={ctaUrl}>{ctaText}</Anchor>
        </EmptyState>
      );
    }
  };

  // should only render if we're not at the bottom and there's a new AI message
  // override if we're too close to bottom
  const shouldRenderScrollToNewMessageButton = isUserViewingLatestMessages()
    ? false
    : showNewMessageCta && currentScroll < maxScroll;

  const onChatError = (error: Error) => {
    throw error;
  };

  const onDisplayPromptLibraryCalled = () => {
    if (onDisplayPromptLibrary) {
      onDisplayPromptLibrary({
        chatToolType: ChatButtonToolType.PROMPT_LIBRARY_SUGGESTION,
        displayPrompt: inputFieldValue?.substring(1),
      });
      setDisplayPromptLibrary(true);
      setOpenPromptLibrary(true);
    }
  };

  const handlePromptSuggestionSelected = (prompt: any) => {
    setValue("input", prompt.template);
    setPromptSuggestion(prompt.template);
    setDisplayPromptLibrary(false);
    setOpenPromptLibrary(false);
  };

  const renderChatComposer = (): ReactElement => {
    return isLiveChatActive ? (
      <>
        <EndChat />
        <LiveChatComposer onError={onChatError} />
      </>
    ) : (
      <Controller
        name={ChatFormLabels.input}
        control={control}
        rules={{ required: true, minLength: MIN_INPUT_LENGTH }}
        render={({ field }) => (
          <CompositeChatComposer
            config={{
              namespace: "comms-gpt-message",
              onError: onChatError,
            }}
            disabled={disabled}
            // Tricky: Some Console pages currently require the old pre-chat experience
            ariaLabel={renderAriaLabel[placeholderToUse]}
            // Tricky: Some Console pages currently require the old pre-chat experience
            placeholder={renderPlaceholder[placeholderToUse]}
            required
            {...field}
            data-testid={dataTestIds.wiseOwlChat.chatComposer}
            onFocus={() => {
              if (displayPromptLibrary) {
                setOpenPromptLibrary(true);
              }
            }}
          >
            <ClearEditorPlugin />
            <ChatComposerPlugins.ChatForeslashCommandPlugin
              applicationId={applicationId}
              shouldDisplayPromptLibrary={displayPromptLibrary}
              handleDisplayPromptLibrary={onDisplayPromptLibraryCalled}
            />
            <ChatComposerPlugins.ChatUpdateInputPlugin
              promptSuggestionSelected={promptSuggestion}
            />
            <ChatComposerPlugins.ChatSendButtonPlugin
              loading={loadingConfiguration.submission}
              isChatActive={isChatActive}
              shouldClearInput={isValid && isSubmitting}
              disabled={disabled}
              applicationId={applicationId}
              handleOnEnter={handleSubmit(formHandler)}
              handleOnClick={handleSubmit(formHandler)}
              handleChatButtonToolClick={onChatButtonToolClick}
            />
          </CompositeChatComposer>
        )}
      />
    );
  };

  const renderCsatSurveyUI = isSurveyVisible &&
    surveyId &&
    chatId &&
    !isLiveChatActive && <CsatSurvey />;

  const renderPreChat = () => {
    if (isConsoleIngress) {
      return <DeprecatedPreChat />;
    }

    return <NewPreChat />;
  };

  return (
    <WiseOwlAppContainer
      as="div"
      element={WiseOwlElementIds.WISE_OWL_CHAT_CONTAINER}
      isChatActive={isChatActive}
      ingressOkForStyle={!isConsoleIngress}
    >
      <Box
        as={isLiveChatActive ? "div" : "form"}
        height="100%"
        onSubmit={handleSubmit(formHandler)}
        element={WiseOwlElementIds.WISE_OWL_CHAT_FORM}
        paddingLeft="space20"
        paddingRight="space20"
      >
        {/** Live Chat Pre-Engagement Modal **/}
        <LiveChatPreEngagement
          ingress={ingress}
          selectedProductCategory={
            liveChatConfiguration?.preEngagementValues?.productCategory
          }
          message={liveChatConfiguration?.preEngagementValues?.message}
          isOpen={isLiveChatPreEngagementOpen}
          handleToggleVisibility={() => {
            if (handleToggleLiveChatPreEngagement) {
              handleToggleLiveChatPreEngagement();
            }
          }}
          handleStartEngagement={handleStartLiveChatEngagement}
        />
        {variantOptions && renderStateVariant()}

        {!variantOptions && (
          <Box
            element={WiseOwlElementIds.WISE_OWL_CHAT_LOG_AND_INPUT_CONTAINER}
            display="flex"
            flexDirection="column"
            // Tricky: Some Console pages currently require the old pre-chat experience
            alignItems={isChatActive || isConsoleIngress ? undefined : "center"}
            justifyContent={
              isChatActive || isConsoleIngress ? undefined : "center"
            }
            position="relative"
            width="100%"
            height="100%"
            marginX="auto"
          >
            {/* Chat Container (pre/active chat) */}
            <Box
              as="section"
              element={WiseOwlElementIds.WISE_OWL_CHAT_LOG_CONTAINER}
              flexGrow={isChatActive ? 1 : 0}
              overflowX={isChatActive ? "hidden" : undefined}
              overflowY={isChatActive ? "auto" : undefined}
              tabIndex={0}
              style={{
                transition: "flex-grow 0.3s ease-in-out 0.2s",
                // Removes double scrollbar when on mobile
                scrollbarWidth: window.innerWidth < 600 ? "none" : undefined,
              }}
            >
              <>
                {shouldRenderScrollToNewMessageButton &&
                  renderScrollToNewMessageButton()}
                {isChatActive ? (
                  <>
                    <ChatLog
                      blocks={blocks}
                      chatMessages={chatMessages}
                      formatDatePastYesterday={
                        constants.DATE_FORMAT_AFTER_YESTERDAY
                      }
                    />
                    {renderCsatSurveyUI}
                  </>
                ) : (
                  renderPreChat()
                )}
              </>
            </Box>

            <Box
              as="section"
              element={
                WiseOwlElementIds.WISE_OWL_CHAT_SUBMISSION_INPUT_CONTAINER
              }
              position="relative"
              width="100%"
              marginX="auto"
              paddingBottom="space50"
            >
              {/* Message input */}
              <Box as="article" position="relative" marginTop="space50">
                {!isLiveChatActive && isSurveyEnabled && (
                  <Box
                    as="div"
                    marginBottom="space60"
                    display="flex"
                    justifyContent="flex-end"
                  >
                    <AssistantFeedbackButton handleClick={handleShowSurvey} />
                  </Box>
                )}
                {renderChatComposer()}

                {openPromptLibrary && (
                  <PromptLibraryContainer
                    handlePromptSuggestionSelected={
                      handlePromptSuggestionSelected
                    }
                    onChatButtonToolClick={onChatButtonToolClick}
                    setOpenPromptLibrary={setOpenPromptLibrary}
                    isChatActive={isChatActive}
                  />
                )}

                {isSubmitted && inputHasMinLengthError && (
                  <HelpText variant="error">
                    {copy.errors.messageMinLength(constants.MIN_INPUT_LENGTH)}
                  </HelpText>
                )}
              </Box>
            </Box>
            {/* Tricky: Some Console pages currently require the old pre-chat experience */}
            {!isChatActive && !isConsoleIngress && (
              <Box as="div" width="100%">
                <PreChatPromptSuggestions />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </WiseOwlAppContainer>
  );
};

export default WiseOwlChat;
